import React, { useState } from 'react';
import { v4 } from 'uuid';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import { useRootStore } from 'app/contexts/RootStoreContext';
import NewCustomDashboardPanelModal from 'shared/components/NewCustomDashboardPanelModal';
import * as custDabrdHelpers from 'usage/containers/CostAndUsageExplorer/helpers/customDashboardHelperMethods';
import Tooltip from 'shared/components/andtComponents/Tooltip';
import { useMainDashboardContext } from 'app/containers/MainDashboard/contexts/mainDashboardContext';
import ReadOnlyDisplayWrapper from 'shared/components/ReadOnlyDisplayWrapper';
import { CategoryAction, HierarchicalEntityCategory } from '@anodot-cost/rbac-client';
import { CostUsageStates } from 'usage/constants/costAndUsageConstants';
import { convertObjToMap } from 'shared/utils/apiUtil';
import { Option } from '../types.ts';
import { GroupByItem } from 'app/containers/MainDashboard/panels/MtdCostBreakdown/types.ts';
import tooltipStyles from 'shared/components/andtComponents/Tooltip.module.scss';
import mainStyles from '../mainDashboardCommon.module.scss';

interface SaveDashboardPanelProps {
  panelType: string;
  groupBy: GroupByItem[] | undefined;
  secondGroupBy?: GroupByItem[];
  orderBy?: Option[];
  granLevel?: string;
  defaultPeriod?: string | number;
  forecastMonths?: number | null;
}

const SaveDashboardPanel: React.FC<SaveDashboardPanelProps> = ({
  panelType,
  groupBy = [],
  orderBy = [],
  secondGroupBy = [],
  granLevel = '',
  defaultPeriod = '',
  forecastMonths = null,
}) => {
  const [dashboardModalIsOpen, setDashboardModalIsOpen] = useState(false);
  const [customPanelType, setCustomPanelType] = useState<string | null>(panelType);

  const { usageStore, usersStore } = useRootStore();
  const { filters, getDynamicFilters } = useMainDashboardContext();
  const cloudType = usersStore?.currDispUserCloudAccountType;
  const { existingDashboardsNamesAndIds } = usageStore.customDbSubStore.customDashboardModel;

  const getParamsForCustomDashboard = () => ({
    id: v4().substring(0, 6).toLowerCase(),
    filtersMap: convertObjToMap(getDynamicFilters().exclude),
  });

  const savePanel = (panelType: string) => {
    setDashboardModalIsOpen(true);
    setCustomPanelType(panelType);
  };

  const getPanelState = () => {
    const dynamicFilters = getDynamicFilters();
    const state: { [key: string]: unknown } = {
      currCustomPanelType: customPanelType,
      filters: filters,
      groupBy: groupBy,
      secondGroupBy: secondGroupBy,
      orderBy: orderBy,
      filterBarGroupBy: groupBy?.[0]?.value,
      selectedGranLevel: granLevel,
      forecastMonths: forecastMonths,
      isAmortize: dynamicFilters.isAmortized ?? false,
      isShowAmortizeCost: dynamicFilters.isAmortized ?? false,
      isDistributed: dynamicFilters.isDistrubuted ?? false,
      isNetAmortize: dynamicFilters.isNetAmortized ?? false,
      isNetUnblended: dynamicFilters.isNetUnblended ?? false,
      isPublicCost: dynamicFilters.isPublicCost ?? false,
      isUnblended: dynamicFilters.isUnblended ?? false,
      isListUnitPrice: dynamicFilters.isListUnitPrice ?? false,
      currCostType: filters.costType?.map((o) => o.value),
    };

    if (secondGroupBy?.length) {
      state.filterBarGroupBySecondary = secondGroupBy[0]?.value;
      state.currDataState = CostUsageStates.SECOND_GROUP_BY;
    }

    if (filters.exclude) {
      state.fieldToFilterdValuesMap = { ...dynamicFilters.exclude };
      const excludeFieldsMap = new Map();
      Object.keys(dynamicFilters.exclude).forEach((key) => {
        excludeFieldsMap.set(key, 1);
      });
      state.excludedFiltersStatusMap = excludeFieldsMap;
    }

    return state;
  };

  return (
    <>
      <div>
        {/*@ts-expect-error legacy component*/}
        <ReadOnlyDisplayWrapper category={HierarchicalEntityCategory.Dashboards} action={CategoryAction.Create}>
          <Tooltip
            title={<span className={tooltipStyles.tooltipText}>Save as Dashboard Panel</span>}
            classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
          >
            <div automation-id="md-create-dashboard-panel">
              <GenerateIcon
                iconName={ICONS.dashboard.name}
                className={mainStyles.dashboardIcon}
                onClick={() => {
                  savePanel(panelType);
                }}
              />
            </div>
          </Tooltip>
        </ReadOnlyDisplayWrapper>
      </div>

      <NewCustomDashboardPanelModal
        modalIsOpen={dashboardModalIsOpen}
        getCurrentCauParams={getParamsForCustomDashboard}
        onClose={() => {
          setDashboardModalIsOpen(false);
          setCustomPanelType(null);
        }}
        customDashboardStore={usageStore.customDbSubStore}
        isSelectTimePeriodEnabled={!!defaultPeriod}
        defaultPeriod={defaultPeriod}
        existingDashboardsNamesAndIds={existingDashboardsNamesAndIds}
        helpers={custDabrdHelpers}
        type={customPanelType}
        state={getPanelState()}
        usageStore={usageStore}
        cloudType={cloudType}
      />
    </>
  );
};

export default SaveDashboardPanel;
